import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Injectable} from '@angular/core';
import {Action, select, Store} from '@ngrx/store';
import {Observable, of as observableOf} from 'rxjs';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    mergeMap,
    switchMap,
    take,
    throttleTime,
    withLatestFrom
} from 'rxjs/operators';

import * as authAction from '@app/views/common/auth.actions';
import * as IntegrationActions from './integration.actions';
import * as ApplicationSettingsActions from '@app/views/admin/application-settings/store/application-settings.actions';
import {State} from './integration.reducers';
import {IntegrationService} from '../../service/integration.service';
import {UserPreferencesService} from '../../service/user-preferences.service';
import {PreferenceItem, Preferences} from '../../model/user-preferences.model';
import {AuthenticatedUserService} from '../../service/authenticated-user.service';
import {User} from '../../model/user';
import {AttributeMetadataModel, DataAccessRequestModel, DataAccessRequestStatus} from '../../model/integration-models';
import {ApplicationsService} from '@app/views/admin/provider/applications/applications.service';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {InstitutionsService} from '@app/views/admin/vendor/institutions/institutions.service';
import {InstitutionsVendorListModel} from '../../model/institutional-model';
import {DataBrowsingService} from '@app/views/databrowsing/databrowsing.service';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {
    getAllVendorApplications,
    getAuthenticatedUser,
    getAuthenticatedUserApplications,
    getAuthenticatedUserPreferences,
    getDeletingApplications
} from '@app/app.reducers';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {SchoolYearRolloverModel} from '@app/core/model/rollover-model';
import {OrgsService} from '@app/core/service/orgs.service';
import {AppGalleryService} from '@app/views/admin/provider/app-gallery/app-gallery.service';
import {ISubcategory, ICategory} from '@app/core/comp/filtering/filter';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';
import {DataBrowser} from '@app/views/databrowsing/data-browser';
import {AuthenticationService} from '@app/core/service/authentication.service';

@Injectable()
export class IntegrationEffects {

    getDefaultIntegration$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.INTEGRATION_GET_DEFAULT),
            map((action: IntegrationActions.IntegrationGettingDefaultAction) => action.payload),
            switchMap(payload => {
                if (payload.applicationId) {
                    return this.integrationService.getDefaultIntegration(payload.applicationId)
                        .pipe(
                            map(res => new IntegrationActions.IntegrationGettingDefaultSuccessAction({integration: res})),
                            catchError(error => observableOf(new IntegrationActions.IntegrationGettingDefaultFailedAction(error)))
                        );
                } else {
                    return observableOf(new IntegrationActions.IntegrationGettingDefaultFailedAction(new Error('Application is not exists')));
                }
            }))
    );

    getDefaultSchema$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_DEFAULT_SCHEMA),
            map((action: IntegrationActions.GetDefaultSchemaAction) => action.payload),
            switchMap((payload) => this.dataBrowsingService.getAttributesMetadata(DataSourceType.EduTone)
                .pipe(
                    map((res: AttributeMetadataModel[]) => new IntegrationActions.GetDefaultSchemaSuccessAction({attributeMetadata: res})),
                    catchError(error => observableOf(new IntegrationActions.GetDefaultSchemaFailedAction(error)))
                )))
    );


    getDefaultCategories$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_DEFAULT_CATEGORIES),
            switchMap((payload) => this.appGalleryService.getDefaultCategories()
                .pipe(
                    map((result: ISubcategory[] = []) => {
                        const categoriesMap = new Map<string, ICategory>();
                        result.filter((category: ISubcategory) => !category.parentCategoryGuid)
                            .forEach((category: ISubcategory) => {
                                category.categoryName = AuthenticationService.htmlDecode(category.categoryName);
                                categoriesMap.set(
                                    category.categoryGuid,
                                    {
                                        subcategories: [],
                                        ...category
                                    }
                                );
                            });

                        result.filter((category: ISubcategory) => !!category.parentCategoryGuid)
                            .forEach((category: ISubcategory) => {
                                category.categoryName = AuthenticationService.htmlDecode(category.categoryName);
                                const parentCategory = categoriesMap.get(category.parentCategoryGuid);

                                if (parentCategory) {
                                    parentCategory.subcategories = [category, ...parentCategory.subcategories].sort(
                                        (a: ISubcategory, b: ISubcategory) => a.categoryName.localeCompare(b.categoryName)
                                    );
                                }
                            });

                        return new IntegrationActions.GetDefaultCategoriesSuccessAction(Array.from(categoriesMap.values()));
                    }),
                    catchError(error => observableOf(new IntegrationActions.GetDefaultCategoriesFailedAction(error)))
                )))
    );

    getPendingRequests$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_PENDING_REQUESTS),
            map((action: IntegrationActions.GetPendingRequestsAction) => action.payload),
            withLatestFrom(this.authenticatedUserService.authenticatedUser$),
            exhaustMap(([payload, user]: [{ currentAppId: string }, User]) => {
                if (user) {
                    return this.integrationService.getRequestListForRecipient(user.orgGuid, <string>DataAccessRequestStatus.PENDING)
                        .pipe(
                            withLatestFrom(this.userPreferencesService.preferences$),
                            map(([res, preferences]: [DataAccessRequestModel[], Preferences]) => {
                                let pendingRequests = res;
                                if (this.authenticatedUserService.isVendor(user)) {
                                    const appGuid = payload
                                        ? payload.currentAppId
                                        : preferences ? preferences.getPreferencesItemValue('currentApp') : '';
                                    if (appGuid) {
                                        pendingRequests = res.filter((request: DataAccessRequestModel) => request.applicationGuid === appGuid);
                                    } else {
                                        pendingRequests = [];
                                    }
                                }
                                return new IntegrationActions.GetPendingRequestsSuccessAction({
                                    allPendingRequests: res,
                                    pendingRequests: pendingRequests,
                                });
                            }),
                            catchError(error => observableOf(new IntegrationActions.GetPendingRequestsFailedAction(error)))
                        );
                } else {
                    return observableOf(new IntegrationActions.GetPendingRequestsFailedAction(new Error('User is not authenticated.')));
                }
            }))
    );

    getProviderApplications$: Observable<Action | Action[]> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_PROVIDER_APPLICATIONS),
            map((action: IntegrationActions.GetProviderApplicationsAction) => action.payload),
            withLatestFrom(
                this.store.pipe(
                    select(getAuthenticatedUser),
                    filter((user: User) => user && !!user.orgGuid)
                )
            ),
            switchMap(([payload, user]: [{ orgGuid: string }, User]) => {
                const orgGuid = (payload && payload.orgGuid ? payload.orgGuid : user.orgGuid);
                return this.applicationsService.getApplications(orgGuid)
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getDeletingApplications))),
                        switchMap(([applications, deletingApplications]: [ApplicationProviderListModel[], ApplicationProviderListModel[]]) => {

                            const updatedDeletingApplications = [];

                            applications = applications.filter((app: ApplicationProviderListModel) => {
                                let result = true;

                                if (deletingApplications?.length > 0) {
                                    const isDeletingApplication = deletingApplications
                                        .find((appToBeDeleted: ApplicationProviderListModel) => appToBeDeleted.guid === app.guid);
                                    if (isDeletingApplication) {
                                        updatedDeletingApplications.push(isDeletingApplication);
                                    }

                                    result = !isDeletingApplication;
                                }

                                return result;
                            });

                            return observableOf(
                                new IntegrationActions.GetProviderApplicationsSuccessAction({applications: applications}),
                                new IntegrationActions.SetDeletingApplicationsAction(updatedDeletingApplications)
                            );
                        }),
                        catchError(error => observableOf(new IntegrationActions.GetProviderApplicationsFailedAction({error: error})))
                    );
            }))
    );

    getVendorInstitutions$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_VENDOR_INSTITUTIONS),
            map((action: IntegrationActions.GetVendorInstitutionsAction) => action.payload),
            withLatestFrom(
                this.store.pipe(
                    select(getAuthenticatedUser),
                    filter((user: User) => user && !!user.orgGuid)
                )
            ),
            throttleTime(500),
            switchMap(([payload, user]: [{ currentAppId: string }, User]) => this.institutionsService.getInstitutions(payload.currentAppId)
                .pipe(
                    map((res: InstitutionsVendorListModel[]) => new IntegrationActions.GetVendorInstitutionsSuccessAction({institutions: res})),
                    catchError(error => observableOf(new IntegrationActions.GetVendorInstitutionsFailedAction({error: error})))
                )))
    );

    getAllVendorApplications$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_ALL_VENDOR_APPLICATIONS),
            withLatestFrom(
                this.store.pipe(
                    select(getAuthenticatedUser),
                    filter((user: User) => user && !!user.orgGuid)
                )
            ),
            throttleTime(500),
            switchMap(() => this.appGalleryService.getAllVendorsApplications()
                .pipe(
                    map((res: ApplicationGalleryModel[]) => new IntegrationActions.GetAllVendorApplicationsSuccessAction({allVendorApplications: res})),
                    catchError(error => observableOf(new IntegrationActions.GetAllVendorApplicationsFailedAction({error: error})))
                )))
    );

    updateAppInAllVendorApplications$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.UPDATE_APP_IN_ALL_VENDOR_APPLICATIONS),
            withLatestFrom(
                this.store.pipe(
                    select(getAuthenticatedUser),
                    filter((user: User) => user && !!user.orgGuid)
                )
            ),
            switchMap(([action, user]: [{ payload: ApplicationGalleryModel }, User]) => this.store.pipe(select(getAllVendorApplications))
                .pipe(
                    filter((allApplications: ApplicationGalleryModel[]) => !!allApplications),
                    map((allApplications: ApplicationGalleryModel[]) => {
                        const updatedApp = action.payload;
                        const updatedAppIndex = allApplications.findIndex(app => updatedApp && app.guid === updatedApp.guid);

                        if (updatedAppIndex > -1) {
                            allApplications[updatedAppIndex] = updatedApp;
                        }

                        return new IntegrationActions.GetAllVendorApplicationsSuccessAction({allVendorApplications: allApplications});
                    }),
                    catchError(error => {
                        console.log(error);
                        return observableOf(new IntegrationActions.GetAllVendorApplicationsFailedAction({error: error}));
                    })
                )))
    );

    getSchoolYearRolloverSettings$ = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.GET_SCHOOL_YEAR_DATA_ROLLOVER_SETTINGS),
            map((action: IntegrationActions.GetSchoolYearRolloverSettingsAction) => action),
            concatMap((action: { payload: string }) => this.orgsService.getOrgDataRolloverSettings(action.payload)
                .pipe(
                    mergeMap((dataRolloverSettings: SchoolYearRolloverModel[]) => [new IntegrationActions.SetSchoolYearRolloverSettingsAction(dataRolloverSettings)]),
                    catchError(error => observableOf(new IntegrationActions.GetSchoolYearRolloverSettingsFailedAction({error: error})))
                ))
        )
    );

    updateVendorCurrentApp$ = createEffect(() =>
        this.actions.pipe(
            ofType(IntegrationActions.IntegrationActionTypes.UPDATE_VENDOR_CURRENT_APP),
            map((action: IntegrationActions.UpdateVendorCurrentAppAction) => action.payload),
            withLatestFrom(
                this.store.pipe(
                    select(getAuthenticatedUserApplications),
                    distinctUntilChanged((a: any, b: any) => JSON.stringify(a) == JSON.stringify(b)),
                    filter(vendorApplications => vendorApplications && vendorApplications.length > (-1))
                )
            ),
            filter(() => this.authenticatedUserService.isVendor()),
            switchMap(([payload, vendorApplications]: [{currentAppId: string}, Application[]]) => this.store.pipe(
                select(getAuthenticatedUserPreferences),
                take(1),
                filter(pref => !!pref && this.authenticatedUserService.isVendor()),
                switchMap((preferences: Preferences) => {
                    const currentAppIndex: number = vendorApplications.findIndex(app => app.guid === payload.currentAppId);

                    if (preferences.getPreferencesItemValue('currentApp') == payload.currentAppId) {
                        return observableOf(
                            new IntegrationActions.UpdateVendorCurrentAppActionUnnecessary(payload),
                            new ApplicationSettingsActions.SetIsDeletingApplication(false),
                        );
                    } else if (currentAppIndex > -1) {
                        const currentApp = vendorApplications[currentAppIndex];
                        const userPreferenceItem = new PreferenceItem(currentApp.name, currentApp.guid);

                        return this.userPreferencesService.updatePreferencesItem('currentApp', userPreferenceItem).pipe(
                            switchMap(res => observableOf(
                                new IntegrationActions.IntegrationClearState(),
                                new ApplicationSettingsActions.ApplicationClearState(),
                                new authAction.AuthenticatedUpdateExtraUserPreferencesSuccessAction({userPreferences: res}),
                                new IntegrationActions.UpdateVendorCurrentAppActionSuccess(payload),
                                new ApplicationSettingsActions.SetIsDeletingApplication(false),
                                new IntegrationActions.GetPendingRequestsAction(),
                                new IntegrationActions.GetVendorInstitutionsAction({currentAppId: currentApp.guid}),
                                new IntegrationActions.IntegrationGettingDefaultAction({applicationId: currentApp.guid})
                            ))
                        );
                    } else if (payload.currentAppId === '') {
                        const userPreferenceItem = new PreferenceItem('', '');
                        this.dataBrowser.clearData();

                        return this.userPreferencesService.updatePreferencesItem('currentApp', userPreferenceItem)
                            .pipe(switchMap(res => observableOf(
                                new authAction.AuthenticatedUpdateExtraUserPreferencesSuccessAction({userPreferences: res}),
                                new IntegrationActions.IntegrationClearState(),
                                new ApplicationSettingsActions.ApplicationClearState(),
                                new IntegrationActions.UpdateVendorCurrentAppActionSuccess(payload),
                                new ApplicationSettingsActions.SetIsDeletingApplication(false),
                                ))
                            );
                    } else {
                        return observableOf(
                            new IntegrationActions.UpdateVendorCurrentAppActionFailed({error: new Error('Wrong Current Application Guid')}),
                            new ApplicationSettingsActions.SetIsDeletingApplication(false)
                        );
                    }
                })
            ))
        )
    );

    /**
     * @constructor
     * @param actions
     * @param integrationService
     * @param userPreferencesService
     * @param authenticatedUserService
     * @param applicationsService
     * @param dataBrowsingService
     * @param dataBrowser
     * @param institutionsService
     * @param orgsService
     * @param appGalleryService
     * @param {Store<State>} store
     */
    constructor(private actions: Actions,
                private integrationService: IntegrationService,
                private userPreferencesService: UserPreferencesService,
                private authenticatedUserService: AuthenticatedUserService,
                private applicationsService: ApplicationsService,
                private dataBrowsingService: DataBrowsingService,
                private dataBrowser: DataBrowser,
                private institutionsService: InstitutionsService,
                private orgsService: OrgsService,
                private appGalleryService: AppGalleryService,
                private store: Store<State>) {
    }
}
